/**
 * generatePassportCard(passport) — renders a shareable image of a traveler's
 * passport onto an off-screen HTML5 canvas. Returns a Promise resolving to a
 * PNG Blob suitable for `navigator.share` (files) or downloading.
 *
 * Design intent: vintage postcard aesthetic matching the on-page passport.
 * Amber/rust gradient background, generous typography, the four core stats
 * in a grid, and up to 6 "stamps" (mini cover thumbnails) at the bottom.
 *
 * Dimensions: 1080 × 1350 (Instagram Stories / Feed portrait sweet spot).
 * Works offline — no external font loads.
 */

const W = 1080;
const H = 1350;

const drawRoundedRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

/**
 * Load an image with CORS. Falls back to null on failure so the card still
 * renders — thumbnails are nice-to-have, not required.
 */
const loadImage = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });

const initials = (name) =>
  (name || '?').split(' ').map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

export async function generatePassportCard(passport) {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background — rust/amber gradient
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#7c2d12'); // amber-900
  bg.addColorStop(0.5, '#9a3412'); // orange-900 mid
  bg.addColorStop(1, '#881337'); // rose-900
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Grain-ish noise (subtle dots)
  ctx.fillStyle = 'rgba(255,255,255,0.03)';
  for (let i = 0; i < 400; i++) {
    ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
  }

  // Border
  ctx.strokeStyle = 'rgba(254, 243, 199, 0.35)'; // amber-100 alpha
  ctx.lineWidth = 4;
  ctx.strokeRect(30, 30, W - 60, H - 60);

  // Header tag
  ctx.fillStyle = '#fcd34d'; // amber-300
  ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('★ AUDIOSTRIDE PASSPORT ★', W / 2, 130);

  // Avatar circle + initials/photo
  const avatarSize = 180;
  const avatarX = W / 2 - avatarSize / 2;
  const avatarY = 170;
  const avatarImg = await loadImage(passport.user.avatar_url);
  ctx.save();
  ctx.beginPath();
  ctx.arc(W / 2, avatarY + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (avatarImg) {
    ctx.drawImage(avatarImg, avatarX, avatarY, avatarSize, avatarSize);
  } else {
    ctx.fillStyle = '#fef3c7'; // amber-100
    ctx.fillRect(avatarX, avatarY, avatarSize, avatarSize);
    ctx.fillStyle = '#7c2d12';
    ctx.font = 'bold 72px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials(passport.user.display_name), W / 2, avatarY + avatarSize / 2);
  }
  ctx.restore();
  ctx.textBaseline = 'alphabetic';

  // Avatar ring
  ctx.strokeStyle = 'rgba(254, 243, 199, 0.5)';
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(W / 2, avatarY + avatarSize / 2, avatarSize / 2 + 3, 0, Math.PI * 2);
  ctx.stroke();

  // Name
  ctx.fillStyle = '#fef3c7';
  ctx.font = 'bold 60px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(passport.user.display_name, W / 2, avatarY + avatarSize + 80);

  // Cities/countries subtitle
  const t = passport.totals;
  if (t.cities_visited > 0) {
    ctx.fillStyle = '#fdba74'; // orange-300
    ctx.font = '28px system-ui, -apple-system, sans-serif';
    const sub = `Explored ${t.cities_visited} ${t.cities_visited === 1 ? 'city' : 'cities'}`
      + (t.countries_visited > 0 ? ` · ${t.countries_visited} ${t.countries_visited === 1 ? 'country' : 'countries'}` : '');
    ctx.fillText(sub, W / 2, avatarY + avatarSize + 125);
  }

  // Stats grid — 2x2
  const gridTop = 620;
  const gridPad = 60;
  const cellW = (W - gridPad * 2 - 30) / 2;
  const cellH = 120;
  const cells = [
    { label: 'STAMPS', value: t.unique_tours, hint: 'unique tours' },
    { label: 'COMPLETIONS', value: t.total_completions, hint: 'tours finished' },
    { label: 'CITIES', value: t.cities_visited, hint: 'visited' },
    { label: 'STREAK', value: `${passport.streak.current} 🔥`, hint: `best ${passport.streak.longest}` },
  ];
  cells.forEach((cell, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const cx = gridPad + col * (cellW + 30);
    const cy = gridTop + row * (cellH + 20);
    ctx.fillStyle = 'rgba(69, 26, 3, 0.45)'; // dark amber, semi
    drawRoundedRect(ctx, cx, cy, cellW, cellH, 16);
    ctx.fill();
    ctx.strokeStyle = 'rgba(180, 83, 9, 0.5)';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = '#fcd34d';
    ctx.font = 'bold 20px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(cell.label, cx + 22, cy + 32);

    ctx.fillStyle = '#fef3c7';
    ctx.font = 'bold 52px system-ui, -apple-system, sans-serif';
    ctx.fillText(String(cell.value), cx + 22, cy + 86);

    ctx.fillStyle = '#fdba74';
    ctx.font = '20px system-ui, -apple-system, sans-serif';
    ctx.fillText(cell.hint, cx + 22, cy + 110);
  });

  // Cities strip — a stylized world-map card summarizing where the traveler
  // has walked. Sits between the stats grid and the stamps thumbnails.
  // We deliberately DON'T try to draw the actual world silhouette (heavy
  // asset for a canvas-only pipeline); instead we render a warm map-like
  // panel with pins projected from the cities' lat/lng bounding box + a
  // headline number ("N CITIES WALKED"). Looks great on Instagram Stories.
  const walkedPins = (passport.cities_walked || []).filter(
    (c) => c && typeof c.lat === 'number' && typeof c.lng === 'number'
  );
  if (walkedPins.length > 0) {
    const mapX = 60;
    const mapY = 900;
    const mapW = W - 120;
    const mapH = 130;

    // Panel background — dark plum/rust tint with a subtle inner glow
    const mapBg = ctx.createLinearGradient(mapX, mapY, mapX, mapY + mapH);
    mapBg.addColorStop(0, '#451a03');   // amber-950
    mapBg.addColorStop(1, '#2c0e01');
    ctx.fillStyle = mapBg;
    drawRoundedRect(ctx, mapX, mapY, mapW, mapH, 20);
    ctx.fill();
    ctx.strokeStyle = 'rgba(217, 119, 6, 0.55)'; // amber-600 alpha
    ctx.lineWidth = 2;
    ctx.stroke();

    // Decorative latitude contours — three subtle wavy lines. Pure eye-candy
    // so the panel doesn't look like a plain rectangle behind the pins.
    ctx.strokeStyle = 'rgba(251, 191, 36, 0.15)'; // amber-400 alpha
    ctx.lineWidth = 1.5;
    for (let i = 1; i <= 3; i++) {
      const cy = mapY + (mapH / 4) * i;
      ctx.beginPath();
      for (let x = mapX + 10; x <= mapX + mapW - 10; x += 12) {
        const y = cy + Math.sin((x / mapW) * Math.PI * 4 + i) * 4;
        if (x === mapX + 10) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    // Compute bounding box for pin projection. For a single-pin user we
    // fake a small box so the pin sits nicely centered.
    let minLat = walkedPins[0].lat;
    let maxLat = walkedPins[0].lat;
    let minLng = walkedPins[0].lng;
    let maxLng = walkedPins[0].lng;
    for (const p of walkedPins) {
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    }
    // Pad to keep pins away from the edges and give single-pin cards
    // reasonable spread so the pin isn't dead-center on top of the label.
    let latSpan = maxLat - minLat;
    let lngSpan = maxLng - minLng;
    if (latSpan < 2) { const c = (minLat + maxLat) / 2; minLat = c - 5; maxLat = c + 5; latSpan = 10; }
    if (lngSpan < 2) { const c = (minLng + maxLng) / 2; minLng = c - 8; maxLng = c + 8; lngSpan = 16; }
    // Reserve inner padding so pins don't touch the border.
    const innerPad = 30;
    const projectX = (lng) => mapX + innerPad + ((lng - minLng) / lngSpan) * (mapW - innerPad * 2);
    const projectY = (lat) => mapY + innerPad + ((maxLat - lat) / latSpan) * (mapH - innerPad * 2);

    // Pin radius — small log scale keyed to walked distance so a
    // heavily-explored city still stands out. Clamped 6..14 px so pins
    // remain identifiable at story-crop sizes.
    const radiusFor = (meters) => {
      const km = Math.max(0.5, (meters || 0) / 1000);
      const r = 5 + Math.log2(km + 1) * 1.4;
      return Math.max(6, Math.min(14, r));
    };

    // Draw pins — amber-500 fill, amber-100 stroke for pop against the
    // dark plum background.
    for (const p of walkedPins) {
      const px = projectX(p.lng);
      const py = projectY(p.lat);
      // Soft outer glow
      ctx.fillStyle = 'rgba(251, 191, 36, 0.28)';
      ctx.beginPath();
      ctx.arc(px, py, radiusFor(p.distance_meters) + 6, 0, Math.PI * 2);
      ctx.fill();
      // Main pin
      ctx.fillStyle = '#f59e0b'; // amber-500
      ctx.beginPath();
      ctx.arc(px, py, radiusFor(p.distance_meters), 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fef3c7'; // amber-100
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Headline — cities count + total distance walked. Uses the same
    // lifetime numbers that appear on the on-page passport hero.
    const totalKm = (t.lifetime_distance_meters || 0) / 1000;
    const cityCount = walkedPins.length;
    ctx.fillStyle = '#fcd34d';
    ctx.font = 'bold 22px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(
      `${cityCount} ${cityCount === 1 ? 'CITY' : 'CITIES'} WALKED · ${totalKm >= 100 ? totalKm.toFixed(0) : totalKm.toFixed(1)} KM`,
      mapX + 20,
      mapY + 30,
    );
    // Label the top city (largest distance walked) as a lil' text pin
    if (walkedPins[0]?.city) {
      ctx.fillStyle = 'rgba(254, 243, 199, 0.75)';
      ctx.font = '20px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(`Home base: ${walkedPins[0].city}`, mapX + mapW - 20, mapY + 30);
    }
  }

  // Stamp thumbnails row — up to 5 mini circles across
  const topStamps = (passport.stamps || []).slice(0, 5);
  if (topStamps.length > 0) {
    ctx.fillStyle = '#fcd34d';
    ctx.font = 'bold 24px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('★ MY STAMPS ★', W / 2, 1075);

    const stampSize = 110;
    const gap = 20;
    const rowW = topStamps.length * stampSize + (topStamps.length - 1) * gap;
    const startX = (W - rowW) / 2;
    const rowY = 1105;

    const stampImages = await Promise.all(topStamps.map((s) => loadImage(s.cover_image_url)));
    topStamps.forEach((s, i) => {
      const x = startX + i * (stampSize + gap);
      ctx.save();
      ctx.beginPath();
      ctx.arc(x + stampSize / 2, rowY + stampSize / 2, stampSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      const img = stampImages[i];
      if (img) {
        ctx.drawImage(img, x, rowY, stampSize, stampSize);
      } else {
        ctx.fillStyle = '#fef3c7';
        ctx.fillRect(x, rowY, stampSize, stampSize);
        ctx.fillStyle = '#7c2d12';
        ctx.font = 'bold 20px system-ui, -apple-system, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const city = (s.city || s.tour_title || '').split(' ')[0].slice(0, 8);
        ctx.fillText(city, x + stampSize / 2, rowY + stampSize / 2);
      }
      ctx.restore();
      ctx.textBaseline = 'alphabetic';
      ctx.strokeStyle = '#fef3c7';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(x + stampSize / 2, rowY + stampSize / 2, stampSize / 2, 0, Math.PI * 2);
      ctx.stroke();
    });
  }

  // Footer
  ctx.fillStyle = 'rgba(254, 243, 199, 0.85)';
  ctx.font = 'bold 32px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('audio-walk.emergent.host', W / 2, H - 100);
  ctx.fillStyle = 'rgba(254, 243, 199, 0.55)';
  ctx.font = '22px system-ui, -apple-system, sans-serif';
  ctx.fillText('Walk. Listen. Collect stamps.', W / 2, H - 60);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed'))),
      'image/png',
      0.95,
    );
  });
}

/**
 * Try native share with the file. Falls back to downloading the PNG if
 * navigator.canShare isn't available or fails.
 */
export async function sharePassportCard(passport, filenameHint = 'audiostride-passport') {
  const blob = await generatePassportCard(passport);
  const file = new File([blob], `${filenameHint}.png`, { type: 'image/png' });
  // Choose the more impressive share caption based on what the traveller has done.
  // If they've walked in multiple cities, lead with the collector angle; otherwise
  // lead with total tours finished.
  const cityCount = passport.totals?.cities_visited || 0;
  const walkedKm = Math.round((passport.totals?.lifetime_distance_meters || 0) / 1000);
  let shareText;
  if (cityCount >= 2) {
    shareText = `${passport.user.display_name} has walked ${cityCount} cities`
      + (walkedKm > 0 ? ` (${walkedKm} km on foot)` : '')
      + ` with Audiostride.`;
  } else {
    shareText = `${passport.user.display_name} has walked ${cityCount} ${cityCount === 1 ? 'city' : 'cities'} with Audiostride.`;
  }
  const shareData = {
    title: 'My Audiostride Passport',
    text: shareText,
    files: [file],
  };
  if (navigator.canShare?.(shareData) && navigator.share) {
    try {
      await navigator.share(shareData);
      return { ok: true, method: 'share' };
    } catch (e) {
      if (e?.name === 'AbortError') return { ok: false, method: 'abort' };
      // fallthrough to download
    }
  }
  // Fallback — download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenameHint}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true, method: 'download' };
}
